import { useEffect, useState } from "react";
import {
  Accordion,
  Alert,
  Badge,
  Button,
  Code,
  Divider,
  Group,
  List,
  NumberInput,
  Paper,
  Select,
  Stack,
  TagsInput,
  Text,
} from "@mantine/core";
import {
  IconAdjustments,
  IconAlertTriangle,
  IconBell,
  IconInfoCircle,
} from "@tabler/icons-react";
import { jsonGet, jsonPut } from "../api";

/**
 * Alert levels, in the words someone choosing one would use.
 *
 * The classifier's own output is a sentiment float and a 0-1 priority. Exposing those as threshold
 * boxes asks people to calibrate against a distribution they have never seen — they set a number
 * once, get the wrong volume, and switch alerting off entirely.
 */
const ALERT_LEVELS = [
  { value: "off", label: "No emails" },
  { value: "negative", label: "Only negative or reputation-affecting" },
  { value: "actionable", label: "Anything worth acting on" },
  { value: "all", label: "Every mention" },
];

/**
 * What this brand is watching, and the controls to change it.
 *
 * Web monitoring is metered — every delivered match costs credits and a delivery cannot be
 * declined — but until now the whole configuration was a switch and a two-option select buried in
 * an 805-line settings page, and this page showed no configuration at all. Someone spending a
 * budget could not see, let alone shape, what they were paying for.
 *
 * The plain-language description comes from the server, built from the same code that publishes
 * the rule. Restating that derivation here would drift the moment either side changed.
 */
export default function MonitoringConfigPanel({ brand, allowance }) {
  const [config, setConfig] = useState(null);
  const [terms, setTerms] = useState([]);
  const [excluded, setExcluded] = useState([]);
  const [matchMode, setMatchMode] = useState("brandName");
  const [alertLevel, setAlertLevel] = useState("off");
  const [minDr, setMinDr] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!brand?.id) return;
    jsonGet(`brands/${brand.id}/firehose/config`)
      .then((data) => {
        setConfig(data);
        applyServerState(data);
      })
      .catch(() => setConfig(null));
  }, [brand?.id]);

  function applyServerState(data) {
    setTerms(data?.watching?.extraTerms ?? []);
    setExcluded(data?.excludedDomains ?? []);
    setMatchMode(data?.matchMode ?? "brandName");
    setAlertLevel(data?.alerts?.level ?? "off");
    setMinDr(data?.alerts?.minDomainRating ?? 0);
  }

  if (!config) return null;

  const dirty =
    matchMode !== (config.matchMode ?? "brandName") ||
    alertLevel !== (config.alerts?.level ?? "off") ||
    minDr !== (config.alerts?.minDomainRating ?? 0) ||
    !sameSet(terms, config.watching?.extraTerms ?? []) ||
    !sameSet(excluded, config.excludedDomains ?? []);

  async function save() {
    setSaving(true);
    setError(null);
    try {
      const updated = await jsonPut(`brands/${brand.id}/firehose/config`, {
        matchMode,
        extraTerms: terms,
        excludedDomains: excluded,
        alertLevel,
        alertMinDomainRating: minDr,
      });
      setConfig(updated);
      applyServerState(updated);
    } catch (err) {
      setError(err?.message ?? "Could not save. Check the terms and try again.");
    } finally {
      setSaving(false);
    }
  }

  const watching = config.watching ?? {};
  const perMatch = allowance?.creditsPerMatch
    ?? config.creditsPerMatch
    ?? (allowance?.monthlyBudgetCredits && allowance?.monthlyBudgetMentions
      ? Math.round(allowance.monthlyBudgetCredits / allowance.monthlyBudgetMentions)
      : null);

  return (
    <Paper withBorder radius="md">
      <Accordion variant="filled">
        <Accordion.Item value="config">
          <Accordion.Control icon={<IconAdjustments size={18} />}>
            <Group gap="xs">
              <Text fw={600}>What we're watching for</Text>
              {watching.brandName && <Badge size="sm" variant="light">{watching.brandName}</Badge>}
              {watching.domain && <Badge size="sm" variant="light">{watching.domain}</Badge>}
              {(watching.extraTerms?.length ?? 0) > 0 && (
                <Badge size="sm" variant="light" color="grape">
                  +{watching.extraTerms.length} more
                </Badge>
              )}
              {config.pausedReason && (
                <Badge size="sm" variant="light" color="orange">
                  Paused
                </Badge>
              )}
              {config.alerts?.level && config.alerts.level !== "off" && (
                <Badge size="sm" variant="light" color="orange" leftSection={<IconBell size={11} />}>
                  Alerts on
                </Badge>
              )}
            </Group>
          </Accordion.Control>

          <Accordion.Panel>
            <Stack gap="md">
              {(perMatch || allowance) && (
                <Alert color="gray" variant="light" icon={<IconInfoCircle size={16} />}>
                  <Text size="sm">
                    {perMatch
                      ? `Each mention we collect costs ${perMatch} credits.`
                      : "Each mention we collect costs credits."}
                    {allowance?.monthlyBudgetMentions != null && (
                      <>
                        {" "}This month{" "}
                        {(allowance.mentionsThisPeriod ?? 0).toLocaleString()} of{" "}
                        {allowance.monthlyBudgetMentions.toLocaleString()} mentions used
                        {allowance.mentionsRemainingToday != null && (
                          <> — {allowance.mentionsRemainingToday.toLocaleString()} left today</>
                        )}
                        . The budget is paced across the month so it is not spent in the first few days.
                      </>
                    )}
                  </Text>
                </Alert>
              )}

              {config.notes?.length > 0 && (
                <Alert color="blue" variant="light" icon={<IconInfoCircle size={16} />}>
                  <List size="sm" spacing={4}>
                    {config.notes.map((note) => (
                      <List.Item key={note}>{note}</List.Item>
                    ))}
                  </List>
                </Alert>
              )}

              {/* Why nothing is arriving, when the answer is money rather than configuration. The
                  tap is paused in this state — the switch above still reads "on", and without
                  this the feed just stays empty with nothing saying why. */}
              {config.pausedReason && (
                <Alert
                  color="orange"
                  variant="light"
                  icon={<IconAlertTriangle size={16} />}
                  title="Not collecting right now"
                >
                  {config.pausedReason}
                </Alert>
              )}

              {config.ruleError && (
                <Alert color="orange" variant="light">
                  Nothing is being monitored yet: {config.ruleError}. Add a brand name or a website
                  in brand settings.
                </Alert>
              )}

              <Select
                label="Match mode"
                description="Domain-only suits brands whose name is an everyday word, where matching the name would flood the feed"
                data={[
                  { value: "brandName", label: "Brand name and domain" },
                  { value: "domainOnly", label: "Domain only" },
                ]}
                value={matchMode}
                onChange={(value) => value && setMatchMode(value)}
                disabled={!config.canEdit}
                style={{ maxWidth: 400 }}
              />

              <TagsInput
                label="Also watch for"
                description={
                  `Product names, a founder, a common misspelling. Up to ` +
                  `${config.limits?.maxExtraTerms ?? 10}. Each term widens what you pay per match ` +
                  `for${perMatch ? ` — every delivered mention costs ${perMatch} credits` : ""}, so keep them specific.`
                }
                placeholder="Add a term and press Enter"
                value={terms}
                onChange={setTerms}
                maxTags={config.limits?.maxExtraTerms ?? 10}
                disabled={!config.canEdit}
                clearable
              />

              <TagsInput
                label="Never watch these sites"
                description="Aggregators, scrapers, anywhere not worth paying for. Paste a URL or type a domain — we'll reduce it to the host."
                placeholder="example.com"
                value={excluded}
                onChange={setExcluded}
                maxTags={config.limits?.maxExcludedDomains ?? 25}
                disabled={!config.canEdit}
                clearable
              />

              <Divider
                my="xs"
                label={
                  <Group gap={6}>
                    <IconBell size={14} />
                    <Text size="xs">Email alerts</Text>
                  </Group>
                }
                labelPosition="left"
              />

              <Select
                label="Tell me about"
                description={
                  "Mentions are already classified for tone and whether they need a response. " +
                  "This decides which of them reach your inbox."
                }
                data={ALERT_LEVELS}
                value={alertLevel}
                onChange={(value) => value && setAlertLevel(value)}
                disabled={!config.canEdit}
                style={{ maxWidth: 400 }}
              />

              {alertLevel !== "off" && (
                <>
                  <NumberInput
                    label="Ignore sites weaker than"
                    description={
                      "Domain Rating, 0-100. The same sentence on a DR 80 publication and a DR 2 " +
                      "scraper are not the same event. Leave at 0 to be told about both. Mentions " +
                      "with no rating always come through."
                    }
                    min={0}
                    max={100}
                    value={minDr}
                    onChange={(value) => setMinDr(Number(value) || 0)}
                    disabled={!config.canEdit}
                    style={{ maxWidth: 400 }}
                  />

                  <Text size="xs" c="dimmed">
                    At most one email per hour. A burst is collected into a single message rather
                    than sent one at a time, and nothing is dropped to stay under that limit.
                  </Text>
                </>
              )}

              {error && <Alert color="red" variant="light">{error}</Alert>}

              {config.canEdit && (
                <Group justify="space-between">
                  <Text size="xs" c="dimmed">
                    Changes reach the feed within five minutes.
                  </Text>
                  <Button size="xs" onClick={save} loading={saving} disabled={!dirty}>
                    Save changes
                  </Button>
                </Group>
              )}

              {config.rule && (
                <Stack gap={4}>
                  <Text size="xs" c="dimmed">The exact rule we publish:</Text>
                  <Code block style={{ fontSize: 11 }}>{config.rule}</Code>
                </Stack>
              )}
            </Stack>
          </Accordion.Panel>
        </Accordion.Item>
      </Accordion>
    </Paper>
  );
}

/** Order-insensitive comparison — TagsInput reorders freely and that is not an edit. */
function sameSet(a, b) {
  if (a.length !== b.length) return false;
  const left = [...a].sort();
  const right = [...b].sort();
  return left.every((value, i) => value === right[i]);
}
